import React, { useState } from 'react'
import { makeStyles } from '@mui/styles'
import { useDispatch, useSelector } from 'react-redux'
import { user } from 'ampersand-app'
import PropTypes from 'prop-types'

import lang from '@/lang'
import PageWrapper from './components/pageWrapper'
import UserInputs from './components/userInputs'
import TaxConsultantUserInputs from './components/taxConsultantUserInputs'
import { toast } from '@/modules/base/components/toast/toastConfig'
import { isMeinBuero } from '@/utils/build-env'
import { setCompanyData, setIsSaving, resetState } from '@/redux/ducks/onboarding/main-v2'
import AcceptAvvAndAgbForm from './components/acceptAvvAndAgbForm'
import RegisterService from '@/modules/_services/register.service'
import { fetchData } from '@/redux/ducks/user/info'
import { reset as resetWizard } from '@/redux/ducks/wizard/config'
import GtmService, { GtmEventTypes } from '@/modules/_services/gtm.service'

const isMeinbuero = isMeinBuero()

const eventOmo = 'Firmen-/ Nutzerdaten'
const userExperienceForPendo = {
  userExperience01: 'experience office',
  userExperience02: 'experience other software',
  userExperience03: 'experience MB Desktop',
  userExperience04: 'experience no/new',
  userExperience05: 'expereince other',
}

const useStyles = makeStyles(() => ({
  form: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
    margin: 0,
  },
}))

const OnboardingUser = ({ onClose = null }) => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const { tenant: { info: { subscriptions } }, wizard: { config: { step, totalSteps } } } = useSelector(state => state)
  const { passwordData: { password }, ...onboardingData } = useSelector(state => state.onboarding.mainV2)
  const isTaxConsultant = subscriptions?.some(s => s.isTaxConsultant === true)
  const subscriptionTypeTypesLabel = subscriptions?.map(s => s.typeOfSubscription).join(', ') || ''
  const isMeinBueroTaxConsultant = isMeinbuero && !isTaxConsultant

  const [inputsValid, setInputsValid] = useState(false)
  const [phoneValid, setPhoneValid] = useState(false)
  const [avvAgbAccepted, setAvvAgbAccepted] = useState(false)
  const companyData = useSelector(state => state.onboarding.mainV2.companyData)

  const onButtonClick = async() => {
    if (!isTaxConsultant) {
      if (ENV.product === 'meinbuero') {
        GtmService.pushEvent({
          'event': GtmEventTypes.MboOnboarding,
          'mbo-onboarding-step': 'unternehmensdaten-abgeschlossen',
        })
        GtmService.pushEvent({
          'event': GtmEventTypes.MboOnboarding,
          'mbo-onboarding-step': 'personalisierung-abgeschlossen',
        })
        GtmService.pushEvent({
          'event': GtmEventTypes.MboOnboarding,
          'mbo-onboarding-step': 'datenschutz-abgeschlossen',
        })
        dispatch(setIsSaving(true))
        try {
          if (password) onboardingData.password = password
          const { body: { data: { trackingData, ...data } } } = await RegisterService.finishOnboarding(onboardingData)

          dispatch(setIsSaving(false))
          dispatch(resetState())
          dispatch(resetWizard())
          user.token = data.token
          user.store()

          GtmService.pushEvent({
            'event': GtmEventTypes.MboOnboarding,
            'mbo-onboarding-step': 'demomodus-abgeschlossen ',
            'PageURL': '/mboonboarding/complete',
          })

          GtmService.pushEvent({
            'event': GtmEventTypes.OnboardingFinished,
          })

          const temp = document.createElement('div')
          temp.innerHTML = '<img height="1" width="1" style="border-style:none;" alt="" src="//www.googleadservices.com/pagead/conversion/399883343/?label=y_vZCOHozYkCEM_41r4B&amp;guid=ON&amp;script=0" />'
          temp.innerHTML += '<img height="1" width="1" style="border-style:none;" alt="" src="//www.googleadservices.com/pagead/conversion/983105331/?label=-1NRCMzYme4BELP-49QD&amp;guid=ON&amp;script=0" />'
          temp.innerHTML += '<img height="1" width="1" style="border-style:none;" alt="" src="//www.googleadservices.com/pagead/conversion/397450461/?label=KtqTCOyNg4oCEN25wr0B&amp;guid=ON&amp;script=0" />'
          temp.innerHTML += '<img height="1" width="1" style="display:none;" alt="" src="https://px.ads.linkedin.com/collect/?pid=3204225&conversionId=4067465&fmt=gif" />'
          document.body.appendChild(temp)

          if (window.pendo) {
            window.pendo.track('userExperience', {
              productType: userExperienceForPendo[onboardingData.experience],
            })
          }

          onClose && onClose()
          dispatch(fetchData())
        } catch (e) {
          console.error(e)
          toast.showToast({ type: 'error', message: lang.defaultErrorMessage })
          dispatch(setIsSaving(false))
        }
      } else {
        GtmService.pushEvent({
          'event': GtmEventTypes.GaEventHub,
          'eventCategory': 'Onboarding',
          'eventAction': 'Weiter',
          'eventLabel': `${eventOmo} abgeschlossen ${subscriptionTypeTypesLabel} (${step}/${totalSteps})`,
        })
      }
    }
    if (!isMeinbuero && !companyData?.source) {
      dispatch(setCompanyData({ ...companyData, source: lang.customerContactForm.noInfo }))
    }
    if (!inputsValid) {
      toast.showToast({ type: 'error', message: lang.onboardingV2.user.errorToast })
      return false
    }
    if (!phoneValid) {
      const message = lang.onboardingV2.user.toastPhone
      toast.showToast({ type: 'error', message })
      return false
    }
    return true
  }

  const onBackButtonClick = () => {
    if (ENV.product === 'orgamax' && !isTaxConsultant) {
      GtmService.pushEvent({
        'event': GtmEventTypes.GaEventHub,
        'eventCategory': 'Onboarding',
        'eventAction': 'Zurück',
        'eventLabel': `${eventOmo} zurück ${subscriptionTypeTypesLabel} (${step}/${totalSteps})`,
      })
    }
    return true
  }

  return (
    <PageWrapper
      title={isTaxConsultant ? lang.onboardingV2.taxConsultantUser.title : lang.onboardingV2.user.title}
      text={isTaxConsultant
        ? lang.onboardingV2.taxConsultantUser.text
        : isMeinBuero
          ? lang.onboardingV2.user.texts[onboardingData.subscriptionTypeTypes || 'default']
          : lang.onboardingV2.user.texts.default}
      onNextButtonClick={onButtonClick}
      onBackButtonClick={onBackButtonClick}
      disabled={!inputsValid || !phoneValid || (isMeinBueroTaxConsultant && !avvAgbAccepted)}
      isMboOnboarding={isMeinbuero}
      width={isMeinBueroTaxConsultant ? 630 : undefined}
    >
      <form className={classes.form}>
        {isTaxConsultant
          ? <TaxConsultantUserInputs setInputsValid={setInputsValid} setPhoneValid={setPhoneValid} />
          : <>
            <UserInputs setInputsValid={setInputsValid} setPhoneValid={setPhoneValid} isTaxConsultant={isTaxConsultant} />
            {isMeinBueroTaxConsultant && <AcceptAvvAndAgbForm setInputsValid={setAvvAgbAccepted} isTaxConsultant={isTaxConsultant} />}
          </>
        }
      </form>
    </PageWrapper>
  )
}

OnboardingUser.propTypes = {
  onClose: PropTypes.func,
}

export default OnboardingUser
